import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Button, Grid, Stack } from '@material-ui/core';
import BackIcon from '@material-ui/icons/ArrowRightAlt';
import Image from './Image';
import ThemeSwitcher from './ThemeSwitcher';
import AppVersion from './AppVersion';
import { useLanguage } from '../contexts/language';

function SetupStep({
  title,
  pretitle,
  text,
  image,
  media,
  form,
  badge,
  action,
  actionClick,
  auxAction,
  auxActionClick,
  next,
  disabled,
  themeSwitcher,
  stepNav,
  ...p
}) {
  return (
    <Stack justifyContent="center" alignItems="center" width="100%" height="100%">
      <Stack
        flex="1 1 auto"
        justifyContent="center"
        alignItems="center"
        width="90%"
        maxWidth="600px"
        spacing="3vh"
        mb="3vh"
        textAlign="center"
        role="main"
      >
        {pretitle && <Typography variant="pretitle">{pretitle}</Typography>}
        {title && <Typography variant="title">{title}</Typography>}
        {text && <Typography variant="text">{text}</Typography>}
        <Stack flex="1 1 0" maxHeight="max-content" overflow="auto" alignItems="center" width="100%">
          {media}
          {image && <Image i={image} style={{ height: '15vh' }} />}
          {form}
        </Stack>
        {badge && <Box>{badge}</Box>}
        {action && (
          <Box width="100%">
            <Button
              color="primary"
              variant="contained"
              disabled={disabled}
              {...(actionClick ? { onClick: actionClick } : { component: Link, to: next })}
              fullWidth
              data-testid="setup-step-action-btn"
              aria-label={action}
            >
              {action}
            </Button>
          </Box>
        )}
        {auxAction && auxActionClick && (
          <Box>
            <Button
              color="primary"
              variant="contained"
              mt={2}
              disabled={disabled}
              fullWidth
              onClick={(e) => auxActionClick(e)}
              aria-label={auxAction}
            >
              {auxAction}
            </Button>
          </Box>
        )}
      </Stack>
      <Stack mb="2vh" width="100%" alignItems="center">
        {stepNav && <StepNav {...p} />}
        {themeSwitcher && <ThemeSwitcher />}

        <Stack spacing={2} direction="row" alignItems="center" justifyContent="end" width="100%" pr={1}>
          <AppVersion />
        </Stack>
      </Stack>
    </Stack>
  );
}

function StepNav({ step = 1, steps = [], back }) {
  const { content } = useLanguage();
  const totalSteps = Array.isArray(steps) ? steps.length : Number(steps) || 0;
  const currentStep = Math.min(Math.max(step, 1), totalSteps || step);
  const srSentenceTemplate =
    content('aria_onboarding_step_sentence') ?? 'Onboarding step number {current} out of {total}';
  const srSentence = srSentenceTemplate.replace('{current}', currentStep).replace('{total}', totalSteps);
  const backLabel = content('back');

  return (
    <Grid container alignItems="center" height="50px" width="10rem">
      <Grid item xs={3} align="center">
        {back && (
          <Link to={back} aria-label={backLabel} title={backLabel}>
            <Typography fontSize="1rem" color="textPrimary">
              <BackIcon style={{ transform: 'rotate(-180deg)', fontSize: '40px' }} aria-hidden="true" />
            </Typography>
          </Link>
        )}
      </Grid>
      <Grid item xs={6} align="center">
        <Box position="relative">
          <Typography fontSize="1.5rem" color="textPrimary" aria-hidden>
            {currentStep}/{totalSteps || '—'}
          </Typography>
          {totalSteps > 0 && (
            <Typography component="span" className="sr-only">
              {srSentence}
            </Typography>
          )}
        </Box>
      </Grid>
      <Grid item xs={3} />
    </Grid>
  );
}

export default SetupStep;
